import React from 'react';
import AlertBox, { Heading } from 'components/shared/AlertBox';
import {
  createRecordingElement,
  withZiggeo,
} from 'components/ui/ZiggeoVideo/ziggeoHelpers';
import { routes } from 'constants/config';
import { Button, ButtonGroup, Spinner } from 'ui';
import request from 'utils/request_ajax';
import trackEvent from 'misc/trackEvent';
import Recorder from 'ui/MediaRecorder/Recorder';
import ZiggeoMediaPlayer from 'ui/ZiggeoVideo/ZiggeoMediaPlayer';

class WebcamRecordingQuestion extends React.Component {
  state = {
    isComplete: false,
    token: null,
    tokenError: false,
    offlineError: false,
    tryAgainModalShown: false,
    uploadTypeError: false,
    provider: null,
  };

  recordingElementRef = React.createRef();

  handleBadUploadType = () => {
    this.setState({ uploadTypeError: true });
  };

  getToken = () => {
    // TODO - once we have switched over completely to react ziggeo remove this function
    if (this.props.hasZiggeoForAllMedia) return '';
    return request
      .get(routes.ZIGGEO_TOKEN.SHOW)
      .then(data => {
        const { success, token } = data;
        if (success) {
          this.setState({ token, provider: 'ziggeo' });
        } else {
          this.setState({
            tokenError: true,
          });
        }
      })
      .fail(() => {
        this.setState({
          tokenError: true,
        });
      });
  };

  createRecordingElement = () => {
    if (!this.props.hasZiggeoForAllMedia) {
      createRecordingElement({
        token: this.state.token,
        saveVideo: this.setAnswer,
        questionId: null,
        webcamPlaybackEnabled: true,
        recordWebcamEvent: this.trackWebcamEvent,
        recordingElement: this.recordingElementRef.current,
        handleBadUploadType: this.handleBadUploadType,
      });
    }
  };

  componentDidMount() {
    if (!this.props.hasZiggeoForAllMedia) {
      this.getToken()
        .then(this.createRecordingElement)
        .then(this.setAccessibilityAttributes);
    }
  }

  setAccessibilityAttributes = () => {
    const { current: recordingElement } = this.recordingElementRef;
    const videoButtons = recordingElement.querySelectorAll(
      '.ba-videorecorder-chooser-button-1 > input',
    );
    if (videoButtons) {
      const uploadVideo = videoButtons[videoButtons.length - 1];
      uploadVideo.setAttribute(
        'aria-label',
        t('questions.question.webcam_recording.upload'),
      );
    }
  };

  toggleTryAgainModal = () => {
    this.setState(state => ({
      tryAgainModalShown: !state.tryAgainModalShown,
    }));
  };

  tryAgain = () => {
    if (navigator && navigator.onLine) {
      trackEvent('practice/recording_try_again_clicked', {
        content_type: 'on_demand_practice_scenario',
        content_id: this.props.id,
      });
      this.props.resetAnswer();
      this.setState(
        { isComplete: false, tryAgainModalShown: false },
        this.createRecordingElement,
      );
    } else {
      this.setState({ offlineError: true });
    }
  };

  closeTypeErrorModal = () => {
    this.setState({
      uploadTypeError: false,
    });
    this.tryAgain();
  };

  closeTokenErrorModal = () => {
    this.setState({
      tokenError: false,
    });
  };

  closeOfflineErrorModal = () => {
    this.setState({
      offlineError: false,
    });
  };

  saveAttempt = cb => {
    this.props.handleSave(this.state.token, cb);
  };

  setAnswer = data => {
    if (data) {
      const { token, provider } = data;
      this.setState({ token, provider });
    }

    if (this.props.setAnswer) {
      this.props.setAnswer(this.state.token, this.state.provider);
    }
    this.setState({ isComplete: true });
  };

  trackWebcamEvent = () => {
    // TODO: Add tracking to ensure ziggeo videos are saved
  };

  render() {
    const {
      isComplete,
      token,
      tokenError,
      offlineError,
      tryAgainModalShown,
      uploadTypeError,
    } = this.state;
    const { apiKey, hasZiggeoForAllMedia } = this.props;
    const hasNoErrors = !offlineError && !uploadTypeError;
    return (
      <>
        <div className="RecordingQuestion VideoEmbed">
          {!token && !hasZiggeoForAllMedia && (
            <div className="d-f h-150 jc-c ai-c">
              <Spinner />
            </div>
          )}
          {hasZiggeoForAllMedia ? (
            <>
              {hasNoErrors && !isComplete && (
                <Recorder
                  apiKey={apiKey}
                  webcamPlaybackEnabled
                  recordWebcamEvent={this.trackWebcamEvent}
                  handleBadUploadType={this.handleBadUploadType}
                  saveMedia={this.setAnswer}
                />
              )}
              {isComplete && (
                <ZiggeoMediaPlayer
                  token={token}
                  apiKey={apiKey}
                  className="maxh-600"
                />
              )}
            </>
          ) : (
            <div
              className="VideoEmbed-videoRecorder mt-8"
              ref={this.recordingElementRef}
            />
          )}

          {isComplete && (
            <ButtonGroup className="mt-16" alignEnd>
              <Button onClick={this.toggleTryAgainModal}>
                {t('common.try_again')}
              </Button>
            </ButtonGroup>
          )}
        </div>
        {tokenError && (
          <AlertBox onDismiss={this.closeTokenErrorModal}>
            <Heading>
              {t(
                'practice.on_demand_practice.webcam_recording.token_error.title',
              )}
            </Heading>
            {t(
              'practice.on_demand_practice.webcam_recording.token_error.description',
            )}
            <ButtonGroup alignEnd className="mt-8">
              <Button customColor onClick={this.closeTokenErrorModal}>
                {t('common.ok')}
              </Button>
            </ButtonGroup>
          </AlertBox>
        )}
        {offlineError && (
          <AlertBox onDismiss={this.closeOfflineErrorModal}>
            <Heading>
              {t('practice.on_demand_practice.recording.offline_error.title')}
            </Heading>
            {t(
              'practice.on_demand_practice.recording.offline_error.description',
            )}
            <ButtonGroup alignEnd className="mt-8">
              <Button customColor onClick={this.closeOfflineErrorModal}>
                {t('common.ok')}
              </Button>
            </ButtonGroup>
          </AlertBox>
        )}
        {uploadTypeError && (
          <AlertBox onDismiss={this.closeTypeErrorModal}>
            <Heading>
              {t(
                'practice.on_demand_practice.recording.upload_type_error.title',
              )}
            </Heading>
            {t(
              'practice.on_demand_practice.recording.upload_type_error.description',
            )}
            <ButtonGroup alignEnd className="mt-8">
              <Button customColor onClick={this.closeTypeErrorModal}>
                {t('common.ok')}
              </Button>
            </ButtonGroup>
          </AlertBox>
        )}
        {tryAgainModalShown && (
          <AlertBox onDismiss={this.toggleTryAgainModal}>
            <Heading>{t('common.try_again')}</Heading>
            {t('practice.on_demand_practice.webcam_recording.try_again_note')}
            <ButtonGroup alignEnd className="mt-16">
              <Button onClick={this.toggleTryAgainModal}>
                {t('common.cancel')}
              </Button>
              <Button customColor onClick={this.tryAgain}>
                {t('common.try_again')}
              </Button>
            </ButtonGroup>
          </AlertBox>
        )}
      </>
    );
  }
}

export default withZiggeo(WebcamRecordingQuestion);
